import * as React from "react"
import { router, Link } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Bookmark, Check, ChevronsUpDown, CornerDownRight, Globe, Plus } from "lucide-react"
import { Button } from "@ui/Button"
import { Input } from "@ui/Input"
import { Menu } from "@ui/Menu"
import { Select } from "@ui/Select"
import { Tabs } from "@ui/Tabs"
import { Textarea } from "@ui/Textarea"
import { cn } from "@lib/cn"
import type {
  ApiRequest,
  Credential,
  Environment,
  HttpMethod,
  Project,
  RequestExample,
  RequestRun,
} from "@lib/inertia-types"
import { CodeEditor } from "./CodeEditor"
import { KeyValueEditor } from "./KeyValueEditor"
import { MocksTab } from "./MocksTab"
import { HistoryTab } from "./HistoryTab"

type CredentialSummary = Pick<Credential, "id" | "name" | "kind">

const NO_ENV = "__none__"

const HTTP_METHODS: HttpMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"]
const BODY_TYPES = [
  { value: "none", label: "No body" },
  { value: "json", label: "JSON" },
  { value: "xml", label: "XML" },
  { value: "text", label: "Text" },
  { value: "form_urlencoded", label: "Form URL-encoded" },
] as const

type SaveState = "saved" | "dirty" | "saving"

// The subset of fields an editor row mutates. Both an ApiRequest (scratchpad)
// and a RequestExample satisfy it; method/url/docs stay endpoint-owned and are
// carried alongside for display only.
type Draft = {
  name: string
  http_method: HttpMethod
  url: string
  headers: ApiRequest["headers"]
  query_params: ApiRequest["query_params"]
  body_type: ApiRequest["body_type"]
  body?: string | null
  auth_type: ApiRequest["auth_type"]
  credential_id: number | null
  docs?: string | null
}

function draftFrom(request: ApiRequest, example: RequestExample | null): Draft {
  const source = example ?? request
  return {
    name: request.name, // the endpoint name lives on the left; examples switch via the dropdown
    http_method: request.http_method, // always the endpoint's
    url: request.url, // always the endpoint's
    headers: source.headers,
    query_params: source.query_params,
    body_type: source.body_type,
    body: source.body,
    auth_type: source.auth_type,
    credential_id: source.credential_id,
    docs: request.docs, // endpoint-level
  }
}

export function RequestBuilder({
  project,
  request,
  example = null,
  environments = [],
  credentials = [],
  environmentId = null,
  serviceSlug = null,
  mockToken,
  host = "",
  onEnvironmentChange,
  onSend,
  running = false,
  sendDisabledReason,
  run = null,
  history = [],
  onSelectRun,
}: {
  project: Project
  request: ApiRequest
  example?: RequestExample | null
  environments?: Environment[]
  credentials?: CredentialSummary[]
  environmentId?: number | null
  serviceSlug?: string | null
  mockToken?: string
  host?: string
  onEnvironmentChange?: (id: number | null) => void
  onSend?: (draft: Draft) => void
  running?: boolean
  sendDisabledReason?: string
  run?: RequestRun | null
  history?: RequestRun[]
  onSelectRun?: (run: RequestRun) => void
}) {
  const isExample = !!example
  const [draft, setDraft] = React.useState<Draft>(() => draftFrom(request, example))
  const [saveState, setSaveState] = React.useState<SaveState>("saved")
  const [activeTab, setActiveTab] = React.useState("params")
  const saveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const nameTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // Resolved-settings preview (inherited base URL + auth) is precomputed
  // server-side per environment and shipped in the payload (the example's own
  // overlay when one is selected), so it renders instantly. Reflects the last
  // save; autosave reloads the relevant prop and refreshes it.
  const effective = ((example ?? request).effective ?? {})[environmentId ? String(environmentId) : "none"] ?? null

  // Reset local state when a different request or example is selected. (The
  // parent also remounts via a composite key, so this is belt-and-suspenders.)
  React.useEffect(() => {
    setDraft(draftFrom(request, example))
    setSaveState("saved")
    if (saveTimer.current) clearTimeout(saveTimer.current)
    if (nameTimer.current) clearTimeout(nameTimer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.id, example?.id])

  const persist = React.useCallback(
    (next: Draft) => {
      setSaveState("saving")
      const opts = {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => setSaveState("saved"),
        onError: () => setSaveState("dirty"),
      }
      if (example) {
        // An example overrides params/body/auth only — never method/url/docs.
        const payload = {
          headers: next.headers.map((h) => ({ ...h })),
          query_params: next.query_params.map((q) => ({ ...q })),
          body_type: next.body_type,
          body: next.body,
          auth_type: next.auth_type,
          credential_id: next.credential_id,
        }
        router.patch(
          `/projects/${project.slug}/requests/${request.id}/examples/${example.id}`,
          payload as unknown as Record<string, never>,
          { ...opts, only: ["selected_example", "requests"] },
        )
      } else {
        const payload = {
          http_method: next.http_method,
          url: next.url,
          headers: next.headers.map((h) => ({ ...h })),
          query_params: next.query_params.map((q) => ({ ...q })),
          body_type: next.body_type,
          body: next.body,
          auth_type: next.auth_type,
          credential_id: next.credential_id,
          docs: next.docs,
        }
        router.patch(
          `/projects/${project.slug}/requests/${request.id}`,
          // KeyValueEntry lacks the index signature FormDataConvertible wants.
          payload as unknown as Record<string, never>,
          { ...opts, only: ["selected_request", "requests"] },
        )
      }
    },
    [project.slug, request.id, example],
  )

  function update(patch: Partial<Draft>) {
    setDraft((prev) => {
      const next = { ...prev, ...patch }
      setSaveState("dirty")
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => persist(next), 800)
      return next
    })
  }

  // The name field edits the endpoint name whether or not an example is
  // selected (examples are switched via the dropdown, not renamed here), so it
  // persists to the request on its own debounce, separate from the param save.
  const persistName = React.useCallback(
    (name: string) => {
      setSaveState("saving")
      router.patch(
        `/projects/${project.slug}/requests/${request.id}`,
        { name } as unknown as Record<string, never>,
        {
          preserveScroll: true,
          preserveState: true,
          only: ["selected_request", "requests"],
          onSuccess: () => setSaveState("saved"),
          onError: () => setSaveState("dirty"),
        },
      )
    },
    [project.slug, request.id],
  )

  function updateName(name: string) {
    setDraft((prev) => ({ ...prev, name }))
    setSaveState("dirty")
    if (nameTimer.current) clearTimeout(nameTimer.current)
    nameTimer.current = setTimeout(() => persistName(name), 800)
  }

  // Create a new example seeded from the scratchpad; the controller redirects to
  // (selects) it, so it opens straight away in the builder.
  const addExample = React.useCallback(() => {
    router.post(
      `/projects/${project.slug}/requests/${request.id}/examples`,
      { seed_from_scratchpad: true },
      { preserveScroll: true },
    )
  }, [project.slug, request.id])

  // Capture a past run's response as a new mock variant, then jump to the
  // Mocks tab. The partial reload refreshes selected_request (incl. mocks) and
  // the flash notice toasts via AppLayout.
  const [savingMock, setSavingMock] = React.useState(false)
  const saveRunAsMock = React.useCallback(
    (r: RequestRun) => {
      setSavingMock(true)
      router.post(
        `/projects/${project.slug}/requests/${request.id}/runs/${r.id}/mock`,
        {} as Record<string, never>,
        {
          preserveScroll: true,
          preserveState: true,
          only: ["selected_request"],
          onSuccess: () => setActiveTab("mocks"),
          onFinish: () => setSavingMock(false),
        },
      )
    },
    [project.slug, request.id],
  )

  // Capture a past run's request (query params + body) as a new example. The
  // partial reload refreshes the tree (new example appears) and the flash toasts.
  const [savingExample, setSavingExample] = React.useState(false)
  const saveRunAsExample = React.useCallback(
    (r: RequestRun) => {
      setSavingExample(true)
      router.post(
        `/projects/${project.slug}/requests/${request.id}/runs/${r.id}/example`,
        {} as Record<string, never>,
        {
          preserveScroll: true,
          preserveState: true,
          only: ["requests", "selected_request", "selected_example"],
          onFinish: () => setSavingExample(false),
        },
      )
    },
    [project.slug, request.id],
  )

  // Sending jumps to the History tab so the latest run is shown automatically.
  const send = React.useCallback(
    (next: Draft) => {
      if (!onSend) return
      setActiveTab("history")
      onSend(next)
    },
    [onSend],
  )

  // Cmd/Ctrl+Enter sends.
  React.useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && onSend) {
        e.preventDefault()
        send(draft)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [draft, onSend, send])

  return (
    <div className="@container flex h-full min-h-0 min-w-0 flex-col gap-3 overflow-hidden p-4">
      <div className="flex flex-col items-start gap-2 @xl:flex-row @xl:items-center">
        {/* Name + save state stay paired on one line at every width. */}
        <div className="flex w-full min-w-0 items-center gap-2 @xl:w-auto">
          <Input
            value={draft.name}
            onChange={(e) => updateName(e.target.value)}
            className="min-w-0 flex-1 border-transparent bg-transparent px-1 text-small font-semibold shadow-none @xl:w-auto @xl:max-w-xs @xl:flex-none"
            aria-label="Request name"
          />
          <span className="shrink-0 text-micro text-cloverhound-gray-500">
            {saveState === "saved" ? "Saved" : saveState === "saving" ? "Saving…" : "Unsaved changes"}
          </span>
        </div>
        <ExampleSwitcher
          project={project}
          request={request}
          example={example}
          onAddExample={addExample}
        />
        {onEnvironmentChange && (
          <div className="w-full @xl:ml-auto @xl:w-44 @xl:shrink-0">
            <Select.Root
              value={environmentId ? String(environmentId) : NO_ENV}
              onValueChange={(v) => onEnvironmentChange(v === NO_ENV || !v ? null : Number(v))}
            >
              <Select.Trigger aria-label="Environment" className="h-7">
                <span className="flex items-center gap-1.5 truncate">
                  <Globe className="size-3.5 shrink-0 text-cloverhound-gray-500" aria-hidden="true" />
                  <Select.Value />
                </span>
              </Select.Trigger>
              <Select.Content>
                <Select.Item value={NO_ENV}>No environment</Select.Item>
                {environments.map((env) => (
                  <Select.Item key={env.id} value={String(env.id)}>
                    {env.name}
                    {env.folder_id === null ? " (shared)" : ""}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 @xl:flex-row @xl:items-center">
        <div className="w-full @xl:w-28 @xl:shrink-0">
          <Select.Root
            value={draft.http_method}
            onValueChange={(v) => v && !isExample && update({ http_method: v as HttpMethod })}
            items={HTTP_METHODS.map((m) => ({ value: m, label: m }))}
            disabled={isExample}
          >
            <Select.Trigger
              aria-label="HTTP method"
              className="font-mono font-semibold"
              title={isExample ? "Method comes from the endpoint" : undefined}
            >
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              {HTTP_METHODS.map((m) => (
                <Select.Item key={m} value={m}>
                  {m}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </div>
        <Input
          value={draft.url}
          onChange={(e) => update({ url: e.target.value })}
          placeholder="/customers/:id/balance or https://api.example.com/path"
          className="w-full font-mono @xl:flex-1"
          aria-label="Request URL"
          readOnly={isExample}
          disabled={isExample}
          title={isExample ? "URL comes from the endpoint" : undefined}
        />
        {onSend && (
          <Button
            onClick={() => send(draft)}
            loading={running}
            disabled={!!sendDisabledReason}
            title={sendDisabledReason}
            className="w-full @xl:w-auto"
          >
            Send
          </Button>
        )}
      </div>

      {effective && effective.url !== draft.url && (
        <div className="flex min-w-0 items-center gap-1.5 px-1 font-mono text-micro text-cloverhound-gray-500">
          <CornerDownRight className="size-3 shrink-0" aria-hidden="true" />
          <span className="min-w-0 truncate" title={effective.url}>
            {effective.url}
          </span>
        </div>
      )}

      <Tabs.Root value={activeTab} onValueChange={(v) => setActiveTab(v as string)} className="min-h-0 min-w-0 flex-1">
        {/* Tab row scrolls horizontally on its own instead of widening the
            page when the labels overflow a narrow panel. */}
        <div className="overflow-x-auto">
          <Tabs.List>
            <Tabs.Tab value="params">
            Params
            {countEnabled(draft.query_params) > 0 && <TabCount count={countEnabled(draft.query_params)} />}
          </Tabs.Tab>
          <Tabs.Tab value="headers">
            Headers
            {countEnabled(draft.headers) > 0 && <TabCount count={countEnabled(draft.headers)} />}
          </Tabs.Tab>
          <Tabs.Tab value="body">Body</Tabs.Tab>
          <Tabs.Tab value="auth">Auth</Tabs.Tab>
          <Tabs.Tab value="docs">Docs</Tabs.Tab>
          <Tabs.Tab value="mocks">
            Mocks
            {(request.mocks?.length ?? 0) > 0 && <TabCount count={request.mocks!.length} />}
          </Tabs.Tab>
          {onSend && (
            <Tabs.Tab value="history">
              History
              {history.length > 0 && <TabCount count={history.length} />}
            </Tabs.Tab>
          )}
          </Tabs.List>
        </div>

        <Tabs.Panel value="params" className="min-h-0 flex-1 overflow-auto">
          <KeyValueEditor
            entries={draft.query_params}
            onChange={(query_params) => update({ query_params })}
            keyPlaceholder="param"
            valuePlaceholder="value or {{variable}}"
            addLabel="Add param"
          />
        </Tabs.Panel>

        <Tabs.Panel value="headers" className="min-h-0 flex-1 overflow-auto">
          <KeyValueEditor
            entries={draft.headers}
            onChange={(headers) => update({ headers })}
            keyPlaceholder="Header-Name"
            valuePlaceholder="value or {{variable}}"
            addLabel="Add header"
          />
        </Tabs.Panel>

        <Tabs.Panel value="body" className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto">
          <div className="w-48">
            <Select.Root
              value={draft.body_type}
              onValueChange={(v) => v && update({ body_type: v as ApiRequest["body_type"] })}
              items={BODY_TYPES.map((b) => ({ value: b.value, label: b.label }))}
            >
              <Select.Trigger aria-label="Body type">
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                {BODY_TYPES.map((b) => (
                  <Select.Item key={b.value} value={b.value}>
                    {b.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>
          {draft.body_type !== "none" && (
            <CodeEditor
              value={draft.body ?? ""}
              onChange={(body) => update({ body })}
              language={draft.body_type === "json" ? "json" : draft.body_type === "xml" ? "xml" : "text"}
              placeholder={
                draft.body_type === "json"
                  ? '{\n  "key": "value"\n}'
                  : draft.body_type === "xml"
                    ? '<root>\n  <key>value</key>\n</root>'
                    : "Request body"
              }
              minHeight="160px"
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="auth" className="min-h-0 flex-1 overflow-auto">
          <div className="max-w-sm">
            <Select.Root
              value={draft.auth_type}
              onValueChange={(v) => v && update({ auth_type: v as ApiRequest["auth_type"] })}
              items={[
                { value: "inherit", label: "Inherit from folder" },
                { value: "none", label: "No auth" },
                { value: "credential", label: "Credential" },
              ]}
            >
              <Select.Trigger aria-label="Auth type">
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                <Select.Item value="inherit">Inherit from folder</Select.Item>
                <Select.Item value="none">No auth</Select.Item>
                <Select.Item value="credential">Credential</Select.Item>
              </Select.Content>
            </Select.Root>
            {draft.auth_type === "credential" && (
              <div className="mt-3">
                {credentials.length === 0 ? (
                  <p className="text-mini text-cloverhound-gray-600">
                    No credentials in this project yet — add one on the Credentials page.
                  </p>
                ) : (
                  <Select.Root
                    value={draft.credential_id ? String(draft.credential_id) : ""}
                    onValueChange={(v) => v && update({ credential_id: Number(v) })}
                  >
                    <Select.Trigger aria-label="Credential">
                      <Select.Value placeholder="Pick a credential…" />
                    </Select.Trigger>
                    <Select.Content>
                      {credentials.map((c) => (
                        <Select.Item key={c.id} value={String(c.id)}>
                          {c.name}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                )}
              </div>
            )}
            {draft.auth_type === "inherit" && effective && (
              <p className="mt-2 flex flex-wrap items-center gap-1.5 text-mini text-cloverhound-gray-600">
                {effective.auth_type === "credential" && effective.credential ? (
                  <>
                    <span>Inherits</span>
                    <span className="rounded-sm bg-cloverhound-gray-100 px-1.5 py-0.5 font-mono text-cloverhound-gray-700 dark:bg-cloverhound-gray-850 dark:text-cloverhound-gray-300">
                      {effective.credential.name}
                    </span>
                    <span>from the folder chain.</span>
                  </>
                ) : (
                  <span>No auth inherited from the folder chain.</span>
                )}
              </p>
            )}
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="docs" className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto">
          {isExample ? (
            // Docs are endpoint-level; show them read-only while editing an example.
            <>
              <p className="text-mini text-cloverhound-gray-500">
                Docs belong to the endpoint. Select the request&rsquo;s Scratchpad to edit them.
              </p>
              {draft.docs?.trim() ? (
                <div className="prose prose-sm max-w-none border-t border-cloverhound-gray-250 pt-3 dark:prose-invert dark:border-cloverhound-gray-800">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft.docs}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-mini text-cloverhound-gray-600">No docs for this endpoint yet.</p>
              )}
            </>
          ) : (
            <>
              <Textarea
                value={draft.docs ?? ""}
                onChange={(e) => update({ docs: e.target.value })}
                placeholder="Document this endpoint (markdown supported)…"
                rows={6}
                className="font-mono text-mini"
              />
              {draft.docs?.trim() && (
                <div className="prose prose-sm max-w-none border-t border-cloverhound-gray-250 pt-3 dark:prose-invert dark:border-cloverhound-gray-800">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft.docs}</ReactMarkdown>
                </div>
              )}
            </>
          )}
        </Tabs.Panel>

        {/* Kept mounted so MocksTab keeps its selection and observes a
            variant captured from the History tab arrive in props — otherwise
            it remounts on tab-switch and resets to the first mock. */}
        <Tabs.Panel value="mocks" keepMounted className="min-h-0 flex-1 overflow-hidden">
          <MocksTab
            project={project}
            request={request}
            environments={environments}
            serviceSlug={serviceSlug}
            environmentId={environmentId}
            mockToken={mockToken}
            host={host}
          />
        </Tabs.Panel>

        {onSend && (
          <Tabs.Panel value="history" className="min-h-0 flex-1 overflow-hidden">
            <HistoryTab
              run={run}
              running={running}
              history={history}
              examples={request.examples ?? []}
              activeExampleId={example?.id ?? null}
              onSelectRun={onSelectRun}
              onSaveAsMock={saveRunAsMock}
              savingMock={savingMock}
              onSaveAsExample={saveRunAsExample}
              savingExample={savingExample}
            />
          </Tabs.Panel>
        )}
      </Tabs.Root>
    </div>
  )
}

// The example switcher sits to the right of the endpoint name: it shows the
// selected example's name (or "Examples" on the scratchpad) and lets you jump
// between the scratchpad, any example, or add a new one.
function ExampleSwitcher({
  project,
  request,
  example,
  onAddExample,
}: {
  project: Project
  request: ApiRequest
  example: RequestExample | null
  onAddExample: () => void
}) {
  const examples = request.examples ?? []
  const base = `/projects/${project.slug}/requests/${request.id}`
  const only = ["selected_request", "selected_example"]

  return (
    <Menu.Root>
      <Menu.Trigger
        className={cn(
          "flex w-full min-w-0 items-center gap-1.5 rounded-md border px-2 py-1 text-small shadow-xs transition-colors",
          "border-cloverhound-gray-200 bg-white hover:bg-cloverhound-gray-100 dark:border-cloverhound-gray-800 dark:bg-cloverhound-gray-950 dark:hover:bg-cloverhound-gray-850",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloverhound-indigo-500",
          "@xl:w-auto @xl:max-w-[16rem]",
        )}
        aria-label="Select example"
      >
        <Bookmark className="size-3.5 shrink-0 text-cloverhound-gray-500" aria-hidden="true" />
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-left",
            !example && "text-cloverhound-gray-500",
          )}
        >
          {example ? example.name : "Examples"}
        </span>
        <ChevronsUpDown className="size-3.5 shrink-0 text-cloverhound-gray-500" aria-hidden="true" />
      </Menu.Trigger>
      <Menu.Content align="start" sideOffset={4} className="min-w-[12rem]">
        <Menu.Label>Examples</Menu.Label>
        {/* The list scrolls within a capped height; "Add example" stays pinned
            below it as a fixed footer. */}
        <div className="max-h-64 overflow-y-auto">
          <Menu.Item render={<Link href={base} only={only} preserveScroll preserveState />}>
            <span className="flex-1 truncate">Scratchpad</span>
            {!example && <Check className="size-3.5 shrink-0" aria-hidden="true" />}
          </Menu.Item>
          {examples.length > 0 && <Menu.Separator />}
          {examples.map((ex) => (
            <Menu.Item
              key={ex.id}
              render={<Link href={`${base}/examples/${ex.id}`} only={only} preserveScroll preserveState />}
            >
              <span className="flex-1 truncate">{ex.name}</span>
              {example?.id === ex.id && <Check className="size-3.5 shrink-0" aria-hidden="true" />}
            </Menu.Item>
          ))}
        </div>
        <Menu.Separator />
        <Menu.Item onClick={onAddExample}>
          <Plus className="size-3.5 shrink-0" aria-hidden="true" />
          Add example
        </Menu.Item>
      </Menu.Content>
    </Menu.Root>
  )
}

function countEnabled(entries: { enabled: boolean; key: string }[]) {
  return entries.filter((e) => e.enabled && e.key).length
}

function TabCount({ count }: { count: number }) {
  return (
    <span className="rounded-pill bg-cloverhound-gray-150 px-1.5 text-micro font-semibold text-cloverhound-gray-700 dark:bg-cloverhound-gray-800 dark:text-cloverhound-gray-300">
      {count}
    </span>
  )
}
